// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { features } from "@/lib/config";
import { SuperadminService } from "@/services/superadmin";
import { BillingOverview } from "@/components/superadmin/billing/BillingOverview";
import { SubscriptionTable } from "@/components/superadmin/billing/SubscriptionTable";
import { PaymentTable } from "@/components/superadmin/billing/PaymentTable";
import { PromotionManager } from "@/components/superadmin/billing/PromotionManager";
import { CreatePromotionDialog } from "@/components/superadmin/billing/CreatePromotionDialog";
import { TrialConfig } from "@/components/superadmin/billing/TrialConfig";
import { PaymentProviderToggle } from "@/components/superadmin/billing/PaymentProviderToggle";

type BillingTab =
  | "overview"
  | "subscriptions"
  | "payments"
  | "promotions"
  | "trials"
  | "providers";

const validTabs: BillingTab[] = [
  "overview",
  "subscriptions",
  "payments",
  "promotions",
  "trials",
  "providers",
];

export interface SearchParams {
  tab: BillingTab;
  // Subscription filters
  subPage: number;
  subSearch: string;
  subPlan: string;
  subStatus: string;
  // Payment filters
  payPage: number;
  payStatus: string;
  payStartDate: string;
  payEndDate: string;
  // Promotion filters
  promoPage: number;
}

export const Route = createFileRoute("/superadmin/billing")({
  beforeLoad: () => {
    if (!features.payments) {
      throw redirect({ to: "/superadmin", search: { timeRange: "30d" } });
    }
  },
  component: BillingPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    tab: (validTabs.includes(search.tab as BillingTab)
      ? search.tab
      : "overview") as BillingTab,
    subPage: Number(search.subPage) || 1,
    subSearch: (search.subSearch as string) || "",
    subPlan: (search.subPlan as string) || "all",
    subStatus: (search.subStatus as string) || "all",
    payPage: Number(search.payPage) || 1,
    payStatus: (search.payStatus as string) || "all",
    payStartDate: (search.payStartDate as string) || "",
    payEndDate: (search.payEndDate as string) || "",
    promoPage: Number(search.promoPage) || 1,
  }),
});

function BillingPage() {
  const { t } = useTranslation("superadmin");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = Route.useSearch();

  // Debounced subscription search
  const [subSearchInput, setSubSearchInput] = useState(params.subSearch);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (subSearchInput !== params.subSearch) {
        updateParams({ subSearch: subSearchInput, subPage: 1 });
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [subSearchInput]);

  useEffect(() => {
    setSubSearchInput(params.subSearch);
  }, [params.subSearch]);

  const updateParams = useCallback(
    (updates: Partial<SearchParams>) => {
      navigate({
        to: "/superadmin/billing",
        search: { ...params, ...updates },
        replace: true,
      });
    },
    [navigate, params],
  );

  const handleTabChange = useCallback(
    (newTab: string) => {
      updateParams({ tab: newTab as BillingTab });
    },
    [updateParams],
  );

  // Promotion dialog state
  const [createPromoOpen, setCreatePromoOpen] = useState(false);

  // ── Queries ──────────────────────────────────────────────────────────

  const overviewQuery = useQuery({
    queryKey: ["superadmin", "billingOverview", "30d"],
    queryFn: () => SuperadminService.getBillingOverview("30d"),
    enabled: params.tab === "overview",
  });

  const subscriptionsQuery = useQuery({
    queryKey: [
      "superadmin",
      "subscriptions",
      params.subPage,
      params.subSearch,
      params.subPlan,
      params.subStatus,
    ],
    queryFn: () =>
      SuperadminService.listSubscriptions({
        page: params.subPage,
        plan: params.subPlan,
        status: params.subStatus,
        search: params.subSearch || undefined,
      }),
    enabled: params.tab === "subscriptions",
  });

  const paymentsQuery = useQuery({
    queryKey: [
      "superadmin",
      "payments",
      params.payPage,
      params.payStatus,
      params.payStartDate,
      params.payEndDate,
    ],
    queryFn: () =>
      SuperadminService.listPaymentTransactions({
        page: params.payPage,
        status: params.payStatus,
        startDate: params.payStartDate || undefined,
        endDate: params.payEndDate || undefined,
      }),
    enabled: params.tab === "payments",
  });

  const promotionsQuery = useQuery({
    queryKey: ["superadmin", "promotions", params.promoPage],
    queryFn: () =>
      SuperadminService.listPromotionCodes({ page: params.promoPage }),
    enabled: params.tab === "promotions",
  });

  const trialConfigQuery = useQuery({
    queryKey: ["superadmin", "trialConfig"],
    queryFn: () => SuperadminService.getTrialConfig(),
    enabled: params.tab === "trials",
  });

  // ── Mutations ────────────────────────────────────────────────────────

  const createPromoMutation = useMutation({
    mutationFn: SuperadminService.createPromotionCode,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["superadmin", "promotions"],
      });
      setCreatePromoOpen(false);
      toast.success(t("common.success"));
    },
    onError: () => toast.error(t("common.failure")),
  });

  const deactivatePromoMutation = useMutation({
    mutationFn: SuperadminService.deactivatePromotionCode,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["superadmin", "promotions"],
      });
      toast.success(t("common.success"));
    },
    onError: () => toast.error(t("common.failure")),
  });

  const updateTrialMutation = useMutation({
    mutationFn: SuperadminService.updateTrialConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["superadmin", "trialConfig"],
      });
      toast.success(t("common.success"));
    },
    onError: () => toast.error(t("common.failure")),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{t("billing.title")}</h1>
        <p className="text-muted-foreground">{t("billing.description")}</p>
      </div>

      <Tabs value={params.tab} onValueChange={handleTabChange}>
        <TabsList className="w-full justify-start overflow-x-auto">
          {validTabs.map((tab) => (
            <TabsTrigger key={tab} value={tab}>
              {t(`billing.tabs.${tab}`)}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <BillingOverview
            data={overviewQuery.data}
            isLoading={overviewQuery.isLoading}
          />
        </TabsContent>

        <TabsContent value="subscriptions" className="mt-6">
          <SubscriptionTable
            subscriptions={subscriptionsQuery.data?.subscriptions ?? []}
            pagination={subscriptionsQuery.data?.pagination}
            isLoading={subscriptionsQuery.isLoading}
            filters={{
              search: subSearchInput,
              plan: params.subPlan,
              status: params.subStatus,
            }}
            onFilterChange={(key, value) =>
              updateParams({
                [`sub${key.charAt(0).toUpperCase()}${key.slice(1)}`]: value,
                subPage: 1,
              } as Partial<SearchParams>)
            }
            onSearchChange={setSubSearchInput}
            onPageChange={(page) => updateParams({ subPage: page })}
            onClearFilters={() =>
              updateParams({
                subSearch: "",
                subPlan: "all",
                subStatus: "all",
                subPage: 1,
              })
            }
          />
        </TabsContent>

        <TabsContent value="payments" className="mt-6">
          <PaymentTable
            transactions={paymentsQuery.data?.transactions ?? []}
            pagination={paymentsQuery.data?.pagination}
            isLoading={paymentsQuery.isLoading}
            filters={{
              status: params.payStatus,
              startDate: params.payStartDate,
              endDate: params.payEndDate,
            }}
            onFilterChange={(key, value) =>
              updateParams({
                [`pay${key.charAt(0).toUpperCase()}${key.slice(1)}`]: value,
                payPage: 1,
              } as Partial<SearchParams>)
            }
            onPageChange={(page) => updateParams({ payPage: page })}
            onClearFilters={() =>
              updateParams({
                payStatus: "all",
                payStartDate: "",
                payEndDate: "",
                payPage: 1,
              })
            }
          />
        </TabsContent>

        <TabsContent value="promotions" className="mt-6">
          <PromotionManager
            promotionCodes={promotionsQuery.data?.promotionCodes ?? []}
            pagination={promotionsQuery.data?.pagination}
            isLoading={promotionsQuery.isLoading}
            onPageChange={(page) => updateParams({ promoPage: page })}
            onCreateClick={() => setCreatePromoOpen(true)}
            onDeactivate={(id) => deactivatePromoMutation.mutate(id)}
            isDeactivating={deactivatePromoMutation.isPending}
          />
          <CreatePromotionDialog
            open={createPromoOpen}
            onOpenChange={setCreatePromoOpen}
            onSubmit={(data) => createPromoMutation.mutate(data)}
            isLoading={createPromoMutation.isPending}
          />
        </TabsContent>

        <TabsContent value="trials" className="mt-6">
          <TrialConfig
            config={trialConfigQuery.data}
            isLoading={trialConfigQuery.isLoading}
            onSave={(data) => updateTrialMutation.mutate(data)}
            isSaving={updateTrialMutation.isPending}
          />
        </TabsContent>

        <TabsContent value="providers" className="mt-6">
          <PaymentProviderToggle />
        </TabsContent>
      </Tabs>
    </div>
  );
}
